import { ImageTuningValues } from 'business/models/ImageTuningModel';
import { CaptureResultTuningValuesDTO } from 'business/models/Models';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { wellData } from './WellImage';

export const getWellNumbersWithinRangeHorizontal = (
  currentWellNumber,
  boundingImagesCount,
  rangeStart,
  rangeEnd,
  excludedNumbers = [],
  plateId,
  exp: ExperimentDtoClient,
) => {
  if (!plateId) {
    return [];
  }
  const plateNumbers = exp.plateDto.map((x) => x.plateNumber);
  const currentPlateNumber = exp.plateDto.find((x) => x.plateId === plateId).plateNumber;
  let numbers: wellData[] = getWellNumbersHorizontal(
    currentWellNumber,
    rangeStart,
    rangeEnd,
    currentPlateNumber,
    plateId,
    boundingImagesCount,
    excludedNumbers,
  );

  const getWellsByPlate = (newPlateNumber: number, newWellNumber: number) => {
    const newPlate = exp.plateDto.find((x) => x.plateNumber === newPlateNumber);
    const newPlateData = getWellNumbersHorizontal(
      newWellNumber,
      1,
      rangeEnd,
      newPlateNumber,
      newPlate?.plateId,
      boundingImagesCount,
      [],
    );
    numbers = numbers.concat(newPlateData);
  };

  if (
    numbers.some(
      (x) =>
        x.wellNum === rangeEnd &&
        x.plateNum === currentPlateNumber &&
        plateNumbers.includes(currentPlateNumber + 1),
    )
  ) {
    getWellsByPlate(currentPlateNumber + 1, 1);
  }
  if (
    numbers.find(
      (x) =>
        x.wellNum === rangeStart &&
        x.plateNum === currentPlateNumber &&
        plateNumbers.includes(currentPlateNumber - 1),
    )
  ) {
    getWellsByPlate(currentPlateNumber - 1, rangeEnd);
  }
  return numbers;
};

const getWellNumbersHorizontal = (
  currentWellNumber,
  rangeStart,
  rangeEnd,
  plateNumber,
  plateId,
  boundingImagesCount,
  excludedNumbers,
) => {
  if (
    rangeStart > rangeEnd ||
    currentWellNumber < rangeStart ||
    currentWellNumber > rangeEnd
  ) {
    return undefined;
  }

  const numbers: wellData[] = [];
  const exclusionSet = new Set(excludedNumbers);
  const start = Math.max(currentWellNumber - boundingImagesCount, rangeStart);
  const end = Math.min(currentWellNumber + boundingImagesCount, rangeEnd);
  for (let i = start; i <= end; i++) {
    if (!exclusionSet.has(i)) {
      numbers.push({ wellNum: i, plateNum: plateNumber, plateID: plateId });
    }
  }
  return numbers;
};

export const areWellDetailsEqual = (imageWellDetails, currentWellDetails) => {
  if (!imageWellDetails || !currentWellDetails) {
    return false;
  }
  const result =
    imageWellDetails.plateID == currentWellDetails.plateID &&
    imageWellDetails.wellNum == currentWellDetails.wellNum &&
    imageWellDetails.cpID == currentWellDetails.cpID &&
    imageWellDetails.dropNumber == currentWellDetails.dropNumber &&
    imageWellDetails.regionName == currentWellDetails.regionName &&
    imageWellDetails.inspectionSet?.id == currentWellDetails.inspectionSet?.id;
  return result;
};

export const getWellNumbersWithinRangeVertically = (
  currentWellNumber,
  boundingImagesCount,
  columns,
  rows,
  excludedNumbers = [],
  plateId,
  exp: ExperimentDtoClient,
) => {
  const totalWells = columns * rows;
  const plateNumbers = exp.plateDto.map((x) => x.plateNumber);
  const currentPlateNumber = exp.plateDto.find((x) => x.plateId === plateId)?.plateNumber;

  let numbers: wellData[] = getWellNumbersVertical(
    totalWells,
    currentWellNumber,
    boundingImagesCount,
    columns,
    rows,
    excludedNumbers,
    plateId,
    currentPlateNumber,
  );

  const getWellsByPlate = (newPlateNumber: number, newWellNumber: number) => {
    const newPlate = exp.plateDto.find((x) => x.plateNumber === newPlateNumber);
    const newPlateData = getWellNumbersVertical(
      totalWells,
      newWellNumber,
      boundingImagesCount,
      columns,
      rows,
      [],
      newPlate.plateId,
      newPlate.plateNumber,
    );
    numbers = numbers.concat(newPlateData);
  };

  if (
    numbers.some((x) => x.wellNum === totalWells && x.plateNum == currentPlateNumber) &&
    plateNumbers.includes(currentPlateNumber + 1)
  ) {
    getWellsByPlate(currentPlateNumber + 1, 1);
  }
  if (
    numbers.find((x) => x.wellNum === 1 && x.plateNum == currentPlateNumber) &&
    plateNumbers.includes(currentPlateNumber - 1)
  ) {
    getWellsByPlate(currentPlateNumber - 1, totalWells);
  }

  return numbers;
};

const getWellNumbersVertical = (
  totalWells,
  currentWellNumber,
  boundingImagesCount,
  columns,
  rows,
  excludedNumbers = [],
  plateId,
  plateNumber,
) => {
  let currentColumnNumber = currentWellNumber % columns;
  if (currentColumnNumber === 0) {
    currentColumnNumber = columns;
  }
  let currentRowNumber = Math.ceil(currentWellNumber / columns);
  if (currentRowNumber === 0) {
    currentRowNumber = 1;
  }

  const wellNumbers: wellData[] = [];
  let currentIndex = currentWellNumber;
  let columnShiftNumber = 1;
  for (let i = 0; i < boundingImagesCount; i++) {
    wellNumbers.push({ wellNum: currentIndex, plateID: plateId, plateNum: plateNumber });
    if (currentIndex === 1) {
      break;
    }
    currentIndex = currentIndex - columns;
    if (currentIndex < currentColumnNumber - (columnShiftNumber - 1)) {
      currentIndex = totalWells - (columns - currentColumnNumber) - columnShiftNumber;
      columnShiftNumber++;
    }
  }
  currentIndex = currentWellNumber;
  columnShiftNumber = 1;
  let lastWellOfColumn = currentWellNumber + columns * (rows - currentRowNumber);
  for (let i = 0; i < boundingImagesCount; i++) {
    wellNumbers.push({ wellNum: currentIndex, plateID: plateId, plateNum: plateNumber });
    if (currentIndex !== lastWellOfColumn) {
      currentIndex = currentIndex + columns;
    }
    if (currentIndex === lastWellOfColumn || currentWellNumber === lastWellOfColumn) {
      if (currentIndex === lastWellOfColumn) {
        wellNumbers.push({
          wellNum: currentIndex,
          plateID: plateId,
          plateNum: plateNumber,
        });
      }
      if (currentIndex === totalWells) {
        wellNumbers.push({
          wellNum: currentIndex,
          plateID: plateId,
          plateNum: plateNumber,
        });
        break;
      }
      lastWellOfColumn = currentIndex + 1;
      currentIndex = currentColumnNumber + columnShiftNumber;
      columnShiftNumber++;
    }
  }

  const result: wellData[] = [];
  wellNumbers.forEach((element) => {
    result.push({
      wellNum: element.wellNum,
      plateID: element.plateID,
      plateNum: element.plateNum,
    });
  });
  return result;
};
export const getUpdatedImageTuningValues = (
  tuningWindowVM: ImageTuningValues,
  captureResultTuningValuesVM: CaptureResultTuningValuesDTO,
): ImageTuningValues => {
  const imageTuning: ImageTuningValues = {};
  imageTuning.autoLevelingLowerLimit = tuningWindowVM.autoLevelingLowerLimit;

  imageTuning.autoLevelingUpperLimit = tuningWindowVM.autoLevelingUpperLimit;

  imageTuning.lowerLevelLimit = tuningWindowVM.lowerLevelLimit;
  imageTuning.upperLevelLimit = tuningWindowVM.upperLevelLimit;
  imageTuning.isAutoLevelingOn =
    captureResultTuningValuesVM == undefined
      ? tuningWindowVM.isAutoLevelingOn
      : captureResultTuningValuesVM.isAutoLevelingOn;

  imageTuning.regionId =
    captureResultTuningValuesVM == undefined
      ? undefined
      : captureResultTuningValuesVM.regionID;

  imageTuning.autoLevelingLowThreshold =
    captureResultTuningValuesVM == undefined
      ? undefined
      : captureResultTuningValuesVM.autoLevelingLowThreshold;

  imageTuning.autoLevelingHighThreshold =
    captureResultTuningValuesVM == undefined
      ? undefined
      : captureResultTuningValuesVM.autoLevelingHighThreshold;

  return imageTuning;
};
